import React, { useState, useEffect } from 'react';
import { config } from '../utils/config';
import { FiPhone, FiMail, FiUser, FiCalendar, FiMapPin, FiRefreshCw, FiActivity, FiX } from 'react-icons/fi';
import { MdOutlineTwoWheeler, MdDirectionsCar, MdDirectionsWalk } from 'react-icons/md';

const DeliveryOrderManagement = () => {
  const [inProgress, setInProgress] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inProgressPagination, setInProgressPagination] = useState({});
  const [historyPagination, setHistoryPagination] = useState({});
  const [inProgressStats, setInProgressStats] = useState({});
  const [globalStats, setGlobalStats] = useState(null);

  // Providers states
  const [providers, setProviders] = useState([]);
  const [providersPagination, setProvidersPagination] = useState({});
  const [providersPage, setProvidersPage] = useState(1);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState(null);
  const providersLimit = 20;

  // Modal State
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [activeOrderTab, setActiveOrderTab] = useState('in-progress');
  const [inProgressPage, setInProgressPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);

  const pageSize = 20;

  const parseEtaMinutes = (etaStr) => {
    if (!etaStr) return null;
    const matches = etaStr.match(/\d+/g);
    if (matches && matches.length > 0) {
      return parseInt(matches[matches.length - 1], 10);
    }
    return null;
  };

  const getEtaDate = (o) => {
    if (!o.estimated_delivery_time) return null;
    const mins = parseEtaMinutes(o.estimated_delivery_time);
    if (mins === null) return null;
    const baseDateStr = o.assigned_at || o.created_at;
    if (!baseDateStr) return null;
    const d = new Date(baseDateStr);
    d.setMinutes(d.getMinutes() + mins);
    return d;
  };

  const fetchProviders = async (page = 1) => {
    setProvidersLoading(true);
    setProvidersError(null);
    try {
      const url = `${config.API_BASE_URL}/kirazee/api/v1/admin/delivery-providers/?page=${page}&limit=${providersLimit}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: Failed to fetch delivery partners`);
      }
      const data = await response.json();
      if (data.success) {
        setProviders(data.providers || []);
        setProvidersPagination(data.pagination || {});
        setGlobalStats({
          current_ongoing_orders_of_all_delivery_partners: data.current_ongoing_orders_of_all_delivery_partners,
          total_delivery_partners: data.total_delivery_partners,
          available_delivery_partners: data.available_delivery_partners,
          busy_delivery_partners: data.busy_delivery_partners,
          delivery_peak_time: data.delivery_peak_time,
          todays_distance: data.todays_distance
        });
      } else {
        throw new Error(data.message || 'Failed to fetch delivery providers');
      }
    } catch (err) {
      console.error('Error fetching providers:', err);
      setProvidersError(err.message);
    } finally {
      setProvidersLoading(false);
    }
  };

  const loadInProgressPage = async (page) => {
    if (!selectedProvider) return;
    setInProgressPage(page);
    setLoading(true);
    try {
      const url = `${config.API_BASE_URL}/kirazee/api/v1/admin/in-progress-orders/?user_id=${encodeURIComponent(selectedProvider.provider_id)}&page=${page}&page_size=${pageSize}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`API error: ${r.status}`);
      const d1 = await r.json();

      let inProgOrders = d1.in_progress_orders ?? d1.orders ?? d1.data ?? [];
      if (!Array.isArray(inProgOrders)) inProgOrders = [];
      const inProgTotal = d1.summary?.total_in_progress_orders ?? d1.summary?.total_orders ?? d1.in_progress_count ?? d1.total_count ?? inProgOrders.length;
      const inProgTotalPages = Math.ceil(inProgTotal / pageSize) || 1;
      const inProgPagination = {
        ...(d1.pagination || {}),
        current_page: d1.pagination?.current_page || page,
        page_size: d1.pagination?.page_size || pageSize,
        total_count: d1.summary?.total_in_progress_orders ?? d1.pagination?.total_items ?? d1.total_count ?? inProgTotal,
        total_pages: d1.pagination?.total_pages || inProgTotalPages,
        has_next: d1.pagination?.has_next ?? (page < inProgTotalPages),
        has_prev: d1.pagination?.has_previous ?? (page > 1)
      };

      setInProgress(inProgOrders);
      setInProgressPagination(inProgPagination);
      if (d1.status_counts || d1.summary?.status_counts) {
        setInProgressStats(d1.status_counts || d1.summary?.status_counts || {});
      }
    } catch (err) {
      console.error('Error fetching in-progress orders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHistoryPage = async (page) => {
    if (!selectedProvider) return;
    setHistoryPage(page);
    setLoading(true);
    try {
      const url = `${config.API_BASE_URL}/kirazee/api/v1/admin/order-history/?user_id=${encodeURIComponent(selectedProvider.provider_id)}&page=${page}&page_size=${pageSize}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`API error: ${r.status}`);
      const d2 = await r.json();

      let historyOrders = d2.orders ?? d2.delivered_orders ?? d2.data ?? [];
      if (!Array.isArray(historyOrders)) historyOrders = [];
      const histTotal = d2.summary?.total_orders ?? d2.total_count ?? historyOrders.length;
      const histTotalPages = Math.ceil(histTotal / pageSize) || 1;
      const historyPag = d2.pagination ?? { current_page: page, page_size: pageSize, total_count: histTotal, total_pages: histTotalPages, has_next: page < histTotalPages, has_prev: page > 1 };

      setHistory(historyOrders);
      setHistoryPagination(historyPag);
    } catch (err) {
      console.error('Error fetching order history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersForProvider = async (providerId, page = 1) => {
    setInProgressPage(1);
    setHistoryPage(1);
    setLoading(true);
    setError(null);
    try {
      const base = `${config.API_BASE_URL}/kirazee/api/v1/admin`;
      const inProgUrl = `${base}/in-progress-orders/?user_id=${encodeURIComponent(providerId)}&page=${page}&page_size=${pageSize}`;
      const historyUrl = `${base}/order-history/?user_id=${encodeURIComponent(providerId)}&page=${page}&page_size=${pageSize}`;

      const [r1, r2] = await Promise.all([fetch(inProgUrl), fetch(historyUrl)]);
      if (!r1.ok) throw new Error(`In-progress API error: ${r1.status}`);
      if (!r2.ok) throw new Error(`Order-history API error: ${r2.status}`);

      const d1 = await r1.json();
      const d2 = await r2.json();

      // Normalize in-progress
      let inProgOrders = d1.in_progress_orders ?? d1.orders ?? d1.data ?? [];
      if (!Array.isArray(inProgOrders)) inProgOrders = [];
      const inProgTotal = d1.summary?.total_in_progress_orders ?? d1.summary?.total_orders ?? d1.in_progress_count ?? d1.total_count ?? inProgOrders.length;
      const inProgTotalPages = Math.ceil(inProgTotal / pageSize) || 1;
      const inProgPagination = {
        ...(d1.pagination || {}),
        current_page: d1.pagination?.current_page || page,
        page_size: d1.pagination?.page_size || pageSize,
        total_count: d1.summary?.total_in_progress_orders ?? d1.pagination?.total_items ?? d1.total_count ?? inProgTotal,
        total_pages: d1.pagination?.total_pages || inProgTotalPages,
        has_next: d1.pagination?.has_next ?? (page < inProgTotalPages),
        has_prev: d1.pagination?.has_previous ?? (page > 1)
      };
      // Normalize history
      let historyOrders = d2.orders ?? d2.delivered_orders ?? d2.data ?? [];
      if (!Array.isArray(historyOrders)) historyOrders = [];
      const histTotal = d2.summary?.total_orders ?? d2.total_count ?? historyOrders.length;
      const histTotalPages = Math.ceil(histTotal / pageSize) || 1;
      const historyPag = d2.pagination ?? { current_page: page, page_size: pageSize, total_count: histTotal, total_pages: histTotalPages, has_next: page < histTotalPages, has_prev: page > 1 };

      setInProgress(inProgOrders);
      setInProgressPagination(inProgPagination);
      setInProgressStats(d1.status_counts || d1.summary?.status_counts || {});
      setHistory(historyOrders);
      setHistoryPagination(historyPag);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders(providersPage);
  }, [providersPage]);

  const getVehicleIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('bike') || t.includes('two') || t.includes('motorcycle') || t.includes('scooter')) {
      return <MdOutlineTwoWheeler style={{ fontSize: 16, marginRight: 6 }} />;
    }
    if (t.includes('car') || t.includes('auto') || t.includes('four')) {
      return <MdDirectionsCar style={{ fontSize: 16, marginRight: 6 }} />;
    }
    return <MdDirectionsWalk style={{ fontSize: 16, marginRight: 6 }} />;
  };

  const getStatusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'available') return 'status-badge available';
    if (s === 'busy') return 'status-badge busy';
    return 'status-badge offline';
  };

  return (
    <div className="dom-container">
      <style>{`
        .dom-container {
          padding: 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        .dom-header-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          border-bottom: 2px solid #edf2f7;
          padding-bottom: 12px;
        }
        .dom-title {
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }
        .dom-btn-primary {
          background: #F55D00;
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          border: none;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .dom-btn-primary:hover {
          background: #e05300;
          box-shadow: 0 4px 12px rgba(245, 93, 0, 0.2);
        }
        .dom-btn-secondary {
          background: #edf2f7;
          color: #4a5568;
          padding: 8px 16px;
          border-radius: 8px;
          border: 1px solid #cbd5e0;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .dom-btn-secondary:hover {
          background: #e2e8f0;
        }
        .providers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }
        .provider-card {
          background: #ffffff;
          border: 1px solid #edf2f7;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .provider-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }
        .provider-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 14px;
          border-bottom: 1px solid #f7fafc;
          padding-bottom: 10px;
        }
        .provider-name {
          font-size: 16px;
          font-weight: 700;
          color: #1a202c;
          margin: 0;
        }
        .provider-id-tag {
          font-size: 11px;
          background: #edf2f7;
          color: #4a5568;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 4px;
          display: inline-block;
        }
        .status-badge {
          padding: 4px 10px;
          border-radius: 9999px;
          font-size: 14px !important;
          font-weight: 600;
          text-transform: capitalize;
        }
        .status-badge.available {
          background-color: #e6fffa;
          color: #047481;
        }
        .status-badge.busy {
          background-color: #feebc8;
          color: #c05621;
        }
        .status-badge.offline {
          background-color: #edf2f7;
          color: #4a5568;
        }
        .provider-details-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .provider-detail-item {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #4a5568;
        }
        .provider-detail-label {
          color: #718096;
          font-weight: 500;
          display: flex;
          align-items: center;
        }
        .provider-detail-value {
          font-weight: 600;
          color: #2d3748;
        }
        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }
        .pagination-info {
          font-size: 14px;
          color: #4a5568;
        }
        .pagination-btn {
          background-color: #fff;
          border: 1px solid #cbd5e0;
          color: #4a5568;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .pagination-btn:hover:not(:disabled) {
          border-color: #F55D00;
          color: #F55D00;
        }
        .pagination-btn:disabled {
          background-color: #edf2f7;
          color: #a0aec0;
          cursor: not-allowed;
          border-color: #edf2f7;
        }
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px;
          font-size: 16px;
          color: #4a5568;
          gap: 10px;
        }
        .spinner {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #F55D00;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 1s linear infinite;
        }
        .spinner-icon-anim {
          animation: spin 1.5s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease-out;
        }
        .modal-container {
          background: #ffffff;
          border-radius: 16px;
          width: 90%;
          max-width: 950px;
          max-height: 85vh;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.05);
          display: flex;
          flex-direction: column;
          animation: slideUp 0.3s ease-out;
          overflow: hidden;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 24px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .modal-title {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }
        .modal-close-btn {
          background: none;
          border: none;
          font-size: 20px;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 6px;
          border-radius: 8px;
          transition: all 0.2s;
        }
        .modal-close-btn:hover {
          background: #e2e8f0;
          color: #0f172a;
        }
        .modal-body {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }
        .modal-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      {!selectedProvider ? (
        <>
          <div className="dom-header-section">
            <h2 className="dom-title">Delivery Order Management</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="dom-btn-secondary" onClick={() => fetchProviders(providersPage)}>
                <FiRefreshCw className={providersLoading ? 'spinner-icon-anim' : ''} /> Refresh
              </button>
            </div>
          </div>

          {globalStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              {[
                { label: 'Ongoing Orders', value: globalStats.current_ongoing_orders_of_all_delivery_partners },
                { label: 'Total Providers', value: globalStats.total_delivery_partners },
                { label: 'Available', value: globalStats.available_delivery_partners },
                { label: 'Busy', value: globalStats.busy_delivery_partners },
                { label: 'Peak Time', value: globalStats.delivery_peak_time || 'N/A' },
                { label: "Today's Distance", value: `${globalStats.todays_distance || 0} km` }
              ].map((stat, i) => (
                <div key={i} style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', marginBottom: '4px' }}>{stat.label}</span>
                  <span style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a' }}>{stat.value}</span>
                </div>
              ))}
            </div>
          )}

          {providersLoading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <span>Loading delivery partners...</span>
            </div>
          )}

          {providersError && (
            <div style={{ color: 'red', marginBottom: 16, fontWeight: 500 }}>{providersError}</div>
          )}

          {!providersLoading && !providersError && providers.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, background: 'white', borderRadius: 8, border: '1px solid #edf2f7', color: '#718096' }}>
              No delivery partners found.
            </div>
          )}

          {!providersLoading && !providersError && providers.length > 0 && (
            <>
              <div className="providers-grid">
                {providers.map(p => (
                  <div key={p.provider_id || Math.random()} className="provider-card">
                    <div>
                      <div className="provider-header">
                        <div>
                          <h4 className="provider-name">{p.name || 'Unnamed Partner'}</h4>
                          <span className="provider-id-tag">ID: {p.provider_id}</span>
                        </div>
                        <span className={getStatusClass(p.status)}>
                          {p.status || 'Offline'}
                        </span>
                      </div>

                      <ul className="provider-details-list">
                        <li className="provider-detail-item">
                          <span className="provider-detail-label"><FiUser style={{ marginRight: 6 }} /> Username</span>
                          <span className="provider-detail-value">{p.name}</span>
                        </li>
                        <li className="provider-detail-item">
                          <span className="provider-detail-label"><FiPhone style={{ marginRight: 6 }} /> Phone</span>
                          <span className="provider-detail-value">{p.phone || 'N/A'}</span>
                        </li>
                        {p.delivery_phone && p.delivery_phone !== p.phone && (
                          <li className="provider-detail-item">
                            <span className="provider-detail-label"><FiPhone style={{ marginRight: 6 }} /> Del. Phone</span>
                            <span className="provider-detail-value">{p.delivery_phone}</span>
                          </li>
                        )}
                        <li className="provider-detail-item">
                          <span className="provider-detail-label"><FiMail style={{ marginRight: 6 }} /> Email</span>
                          <span className="provider-detail-value" style={{ fontSize: 12, wordBreak: 'break-all' }}>{p.email || 'N/A'}</span>
                        </li>
                        <li className="provider-detail-item">
                          <span className="provider-detail-label">
                            {getVehicleIcon(p.vehicle_type)} Vehicle
                          </span>
                          <span className="provider-detail-value">{p.vehicle_type || 'N/A'}</span>
                        </li>

                        <li className="provider-detail-item">
                          <span className="provider-detail-label"><FiMapPin style={{ marginRight: 6 }} /> Distance</span>
                          <span className="provider-detail-value">{p.total_kilometers_traveled || 0} km</span>
                        </li>
                        <li className="provider-detail-item">
                          <span className="provider-detail-label"><FiCalendar style={{ marginRight: 6 }} /> Registered</span>
                          <span className="provider-detail-value" style={{ fontSize: 12 }}>
                            {p.registered_at ? new Date(p.registered_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </li>
                      </ul>
                    </div>

                    <div style={{ marginTop: 16, borderTop: '1px solid #edf2f7', paddingTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        className="dom-btn-primary"
                        style={{ fontSize: 13, padding: '8px 12px', width: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedProvider(p);
                          setInProgress([]);
                          setHistory([]);
                          fetchOrdersForProvider(p.provider_id, 1);
                        }}
                      >
                        View Orders
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {providersPagination && providersPagination.total_pages > 1 && (
                <div className="pagination-container">
                  <span className="pagination-info">
                    Page <strong>{providersPagination.current_page}</strong> of <strong>{providersPagination.total_pages}</strong>
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="pagination-btn"
                      disabled={!providersPagination.has_prev_page || providersLoading}
                      onClick={() => setProvidersPage(prev => Math.max(prev - 1, 1))}
                    >
                      Previous
                    </button>
                    <button
                      className="pagination-btn"
                      disabled={!providersPagination.has_next_page || providersLoading}
                      onClick={() => setProvidersPage(prev => prev + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className="provider-orders-view" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className="dom-header-section">
            <div>
              <h2 className="dom-title">Orders for {selectedProvider.name}</h2>
              <span style={{ fontSize: 14, color: '#64748b', fontWeight: 500, marginTop: 4, display: 'block' }}>Provider ID: {selectedProvider.provider_id}</span>
            </div>
            <button className="dom-btn-secondary" onClick={() => setSelectedProvider(null)}>
              Back to Providers
            </button>
          </div>

          <div className="orders-body" style={{ marginTop: 20 }}>
            {loading && (
              <div className="loading-container">
                <div className="spinner"></div>
                <span>Loading orders...</span>
              </div>
            )}

            {error && (
              <div style={{ color: 'red', marginBottom: 16, fontWeight: 500, textAlign: 'center', background: '#fef2f2', padding: 16, borderRadius: 8 }}>
                {error}
              </div>
            )}

            {!loading && !error && (
              <div className="orders-tab-view">
                <div style={{ display: 'flex', gap: 16, marginBottom: 24, borderBottom: '1px solid #e2e8f0', paddingBottom: 0 }}>
                  <button
                    onClick={() => setActiveOrderTab('in-progress')}
                    style={{
                      background: 'none', border: 'none', padding: '12px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                      color: activeOrderTab === 'in-progress' ? '#F55D00' : '#64748b',
                      borderBottom: activeOrderTab === 'in-progress' ? '2px solid #F55D00' : '2px solid transparent',
                      marginBottom: '-1px'
                    }}
                  >
                    In Progress Orders ({inProgressPagination.total_count ?? inProgressPagination.total_items ?? inProgress.length})
                  </button>
                  <button
                    onClick={() => setActiveOrderTab('history')}
                    style={{
                      background: 'none', border: 'none', padding: '12px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                      color: activeOrderTab === 'history' ? '#F55D00' : '#64748b',
                      borderBottom: activeOrderTab === 'history' ? '2px solid #F55D00' : '2px solid transparent',
                      marginBottom: '-1px'
                    }}
                  >
                    Today's Deliveries ({history.filter(o => {
                      const dateStr = o.updated_at;
                      if (!dateStr) return false;
                      const d = new Date(dateStr);
                      const today = new Date();
                      return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
                    }).length})
                  </button>
                </div>

                <div style={{ background: '#f8fafc', padding: 24, borderRadius: 12, minHeight: 400, border: '1px solid #edf2f7', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.02)' }}>
                  {activeOrderTab === 'in-progress' && (
                    <>
                      {Object.keys(inProgressStats).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
                          {Object.entries(inProgressStats).map(([status, count]) => (
                            <div key={status} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 16px', display: 'flex', flexDirection: 'column', minWidth: '120px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                              <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em', marginBottom: '4px' }}>{status.replace(/_/g, ' ')}</span>
                              <span style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {inProgress.length === 0 ? (
                        <div style={{ color: '#64748b', fontSize: 14, padding: 40, textAlign: 'center', background: '#ffffff', borderRadius: 12, border: '1px dashed #cbd5e0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                          <FiActivity size={32} color="#94a3b8" />
                          <span>No in-progress orders found.</span>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                          {inProgress.map(o => (
                            <div key={o.order_id || o.id || Math.random()} style={{ padding: '20px', background: '#ffffff', border: '1px solid #edf2f7', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)', transition: 'all 0.2s ease' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div>
                                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Order ID</div>
                                  <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>#{o.order_id || o.id}</div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                                  <span style={{ fontWeight: 600, fontSize: 12, color: '#1d4ed8', background: '#dbeafe', padding: '6px 12px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3b82f6' }}></span>
                                    {o.status || 'In Progress'}
                                  </span>
                                  {o.estimated_delivery_time && getEtaDate(o) && (o.completed_at ? new Date(o.completed_at) : new Date()) > getEtaDate(o) && (
                                    <span style={{ fontWeight: 600, fontSize: 11, color: '#ef4444', background: '#fee2e2', padding: '2px 8px', borderRadius: '12px' }}>Delayed</span>
                                  )}
                                </div>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                  <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '8px', color: '#64748b' }}>
                                    <FiUser size={16} />
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: '2px' }}>Business Name</div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#334155', lineHeight: '1.4' }}>{o.business?.name || o.business_name || 'N/A'}</div>
                                  </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                  <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '8px', color: '#64748b' }}>
                                    <FiUser size={16} />
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: '2px' }}>Customer Name</div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#334155' }}>{o.customer?.name || o.customer_name || o.customer?.phone || o.customer_phone || 'N/A'}</div>
                                  </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                  <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '8px', color: '#64748b' }}>
                                    <FiMapPin size={16} />
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: '2px' }}>Delivery Address</div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: '#334155', lineHeight: '1.4' }}>{o.consumer_address || o.customer?.address || 'N/A'}</div>
                                  </div>
                                </div>
                              </div>

                              <div style={{ paddingTop: '16px', borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                                  <FiCalendar size={14} />
                                  {o.assigned_at ? new Date(o.assigned_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : (o.created_at ? new Date(o.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : 'Recently')}
                                  {o.estimated_delivery_time && getEtaDate(o) && (
                                    <>
                                      <span style={{ color: '#cbd5e0', margin: '0 4px' }}>|</span>
                                      <span style={{ color: '#8b5cf6', background: '#ede9fe', padding: '2px 8px', borderRadius: '12px', fontSize: 11, fontWeight: 600 }}>
                                        ETA: {getEtaDate(o).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({o.estimated_delivery_time})
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {inProgressPagination && inProgressPagination.total_pages > 1 && (
                        <div className="pagination-container" style={{ marginTop: 24, borderTop: 'none', paddingTop: 0 }}>
                          <span className="pagination-info">
                            Page <strong>{inProgressPagination.current_page}</strong> of <strong>{inProgressPagination.total_pages}</strong>
                          </span>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              className="pagination-btn"
                              disabled={!(inProgressPagination.has_prev || inProgressPagination.has_prev_page) || loading}
                              onClick={() => loadInProgressPage(Math.max(inProgressPage - 1, 1))}
                            >
                              Previous
                            </button>
                            <button
                              className="pagination-btn"
                              disabled={!(inProgressPagination.has_next || inProgressPagination.has_next_page) || loading}
                              onClick={() => loadInProgressPage(inProgressPage + 1)}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {activeOrderTab === 'history' && (() => {
                    const todaysOrders = history.filter(o => {
                      const dateStr = o.updated_at;
                      if (!dateStr) return false;
                      const d = new Date(dateStr);
                      const today = new Date();
                      return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
                    });

                    return (
                      <>
                        {todaysOrders.length === 0 ? (
                          <div style={{ color: '#64748b', fontSize: 14, padding: 40, textAlign: 'center', background: '#ffffff', borderRadius: 12, border: '1px dashed #cbd5e0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                            <FiActivity size={32} color="#94a3b8" />
                            <span>No orders delivered today.</span>
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                            {todaysOrders.map(o => (
                              <div key={o.order_id || o.id || Math.random()} style={{ padding: '20px', background: '#ffffff', border: '1px solid #edf2f7', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)', transition: 'all 0.2s ease' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                  <div>
                                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Order ID</div>
                                    <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>#{o.order_id || o.id}</div>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                                    <span style={{ fontWeight: 600, fontSize: 12, color: '#059669', background: '#d1fae5', padding: '6px 12px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#059669' }}></span>
                                      {o.status || 'Delivered'}
                                    </span>
                                    {o.estimated_delivery_time && getEtaDate(o) && ((o.completed_at || o.updated_at) ? new Date(o.completed_at || o.updated_at) : new Date()) > getEtaDate(o) && (
                                      <span style={{ fontWeight: 600, fontSize: 11, color: '#ef4444', background: '#fee2e2', padding: '2px 8px', borderRadius: '12px' }}>Delayed</span>
                                    )}
                                  </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                    <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '8px', color: '#64748b' }}>
                                      <FiUser size={16} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: '2px' }}>Business Name</div>
                                      <div style={{ fontWeight: 600, fontSize: 14, color: '#334155', lineHeight: '1.4' }}>{o.business?.name || o.business_name || 'N/A'}</div>
                                    </div>
                                  </div>

                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                    <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '8px', color: '#64748b' }}>
                                      <FiUser size={16} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: '2px' }}>Customer Name</div>
                                      <div style={{ fontWeight: 600, fontSize: 14, color: '#334155' }}>{o.customer?.name || o.customer_name || o.customer?.phone || o.customer_phone || 'N/A'}</div>
                                    </div>
                                  </div>

                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                    <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '8px', color: '#64748b' }}>
                                      <FiMapPin size={16} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: '2px' }}>Delivery Address</div>
                                      <div style={{ fontWeight: 600, fontSize: 14, color: '#334155', lineHeight: '1.4' }}>{o.consumer_address || o.customer?.address || 'N/A'}</div>
                                    </div>
                                  </div>
                                </div>

                                <div style={{ paddingTop: '16px', borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {o.created_at && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                                        <FiCalendar size={14} />
                                        {new Date(o.created_at).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                      </div>
                                    )}
                                    {(o.completed_at || o.updated_at) && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#059669', fontSize: 13, fontWeight: 500 }}>
                                        <FiCalendar size={14} />
                                        {new Date(o.completed_at || o.updated_at).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                      </div>
                                    )}
                                  </div>
                                  {o.estimated_delivery_time && getEtaDate(o) && (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                      <span style={{ color: '#8b5cf6', background: '#ede9fe', padding: '4px 10px', borderRadius: '12px', fontSize: 11, fontWeight: 600 }}>
                                        ETA: {getEtaDate(o).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({o.estimated_delivery_time})
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {Math.ceil(todaysOrders.length / pageSize) > 1 && (
                          <div className="pagination-container" style={{ marginTop: 24, borderTop: 'none', paddingTop: 0 }}>
                            <span className="pagination-info">
                              Page <strong>{historyPage}</strong> of <strong>{Math.ceil(todaysOrders.length / pageSize)}</strong>
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                className="pagination-btn"
                                disabled={historyPage === 1 || loading}
                                onClick={() => setHistoryPage(Math.max(historyPage - 1, 1))}
                              >
                                Previous
                              </button>
                              <button
                                className="pagination-btn"
                                disabled={historyPage >= Math.ceil(todaysOrders.length / pageSize) || loading}
                                onClick={() => setHistoryPage(historyPage + 1)}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryOrderManagement;
